import { captureException } from "@sentry/react";

const RELOAD_AT_KEY = "chunk_reload_at";
// A failure this soon after a reload is the same chunk missing from the build the
// reload landed on. Reloading again would only ping-pong through the rollout.
const RELOAD_COOLDOWN_MS = 10_000;

let preloadsInFlight = 0;
let buildIsStale = false;
// Set once this document has started a full page load; it is on its way out.
let leaving = false;
let onStale: () => void = () => undefined;

/**
 * Runs a speculative load, such as a route preload on hover. A chunk that fails
 * while one is in flight never reloads the page: nothing the user asked for
 * needs it yet, and a reload would discard whatever they were typing. It only
 * marks the build stale; the router then loads the new build at the
 * destination of the next page change (see `freshLoadTarget`).
 */
export async function asPreload<T>(load: () => Promise<T>): Promise<T> {
  preloadsInFlight += 1;
  try {
    return await load();
  } finally {
    preloadsInFlight -= 1;
  }
}

/** Runs `callback` when a preload first finds the deployed build replaced. */
export function onBuildStale(callback: () => void) {
  onStale = callback;
}

type NavigationEnds = { pathname: string; href: string };

/**
 * Where to send a full page load for a navigation, once the build is stale: the
 * navigation's own destination, so the user lands where they were going on the
 * new build. Search-only changes, such as a filter typed into the URL, stay
 * client-side; they need no new code.
 */
export function freshLoadTarget(
  from: NavigationEnds | undefined,
  to: NavigationEnds
): string | null {
  return buildIsStale && !leaving && from?.pathname !== to.pathname
    ? to.href
    : null;
}

/** Full page load of `href`; later chunk failures in this document are ignored. */
export function leaveFor(href: string) {
  leaving = true;
  window.location.assign(href);
}

/**
 * Recovers a tab whose build was deleted underneath it.
 *
 * Dashboard assets are baked into the image and served from the pod, with no CDN
 * or shared object store, so a rollout removes the previous build's chunks as old
 * pods drain. A tab loaded before the rollout holds the old entry point and 404s
 * on any chunk it has not already fetched, and `React.lazy` caches that rejection
 * so the failure never clears itself.
 *
 * Vite dispatches a cancelable `vite:preloadError` for these and rethrows unless
 * something cancels it. Cancelling it and reloading lands on the `no-store`
 * `index.html`, picking up whichever build the serving pod has now.
 *
 * A repeat failure inside the cooldown is deliberately left uncancelled: Vite
 * rethrows, the route error boundary reports it and offers Try Again / Go Home,
 * so a tab that cannot be recovered ends up somewhere actionable rather than in a
 * silent reload loop. Past the cooldown — a later rollout in a long-lived tab —
 * recovery arms again.
 */
export function installChunkReloadHandler() {
  buildIsStale = false;
  leaving = false;
  window.addEventListener("vite:preloadError", (event) => {
    if (leaving) {
      // A full page load is already under way; reloading here would replace it.
      event.preventDefault();
      return;
    }
    if (preloadsInFlight > 0) {
      if (!buildIsStale) {
        buildIsStale = true;
        captureException(event.payload, {
          level: "warning",
          tags: { chunkReload: "stale" },
        });
        onStale();
      }
      return;
    }
    const lastReloadAt = Number(window.sessionStorage.getItem(RELOAD_AT_KEY));
    if (Date.now() - lastReloadAt < RELOAD_COOLDOWN_MS) {
      return;
    }
    event.preventDefault();
    window.sessionStorage.setItem(RELOAD_AT_KEY, String(Date.now()));
    // nginx sets `access_log off` for static assets, so the 404 leaves no trace
    // server-side and this is the only signal that the reload happened.
    captureException(event.payload, {
      level: "warning",
      tags: { chunkReload: "reloaded" },
    });
    leaving = true;
    window.location.reload();
  });
}
